import React, { PropsWithChildren } from 'react'
import { ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { breakpointValues } from '../../tokens/breakpoints'
import { palette } from '../../tokens/colors'
import { buttonThemeOverrides } from '../Button/MUIButton'
import { chipThemeOverrides } from '../Chip/MUIChip'
import { menuThemeOverrides } from '../Menu'
import { dialogThemeOverrides } from '../Dialog'
import { avatarThemeOverrides } from '../Avatar'
import { alertThemeOverrides } from '../Alert'
import { tabsThemeOverrides } from '../Tabs'
import { formLabelThemeOverrides } from '../Form'

const theme = responsiveFontSizes(
  createTheme({
    breakpoints: {
      values: {
        xs: breakpointValues.extraSmall,
        sm: breakpointValues.small,
        md: breakpointValues.medium,
        lg: breakpointValues.large,
        xl: breakpointValues.extraLarge,
      },
    },
    typography: {
      fontFamily: ['Source Sans Pro', 'sans-serif'].join(','),
      h1: {
        fontFamily: ['Merriweather', 'sans-serif'].join(','),
      },
      h2: {
        fontFamily: ['Merriweather', 'sans-serif'].join(','),
      },
      h3: {
        fontFamily: ['Merriweather', 'sans-serif'].join(','),
      },
      h4: {
        fontFamily: ['Merriweather', 'sans-serif'].join(','),
      },
      h5: {
        fontFamily: ['Merriweather', 'sans-serif'].join(','),
      },
      h6: {
        fontFamily: ['Merriweather', 'sans-serif'].join(','),
      },
    },
    palette,
    /* End Migrated */
    components: {
      ...buttonThemeOverrides,
      ...chipThemeOverrides,
      ...menuThemeOverrides,
      ...dialogThemeOverrides,
      ...avatarThemeOverrides,
      ...alertThemeOverrides,
      ...tabsThemeOverrides,
      ...formLabelThemeOverrides,
      /* Migrated from the app; not BS 2.0 */
      MuiListItem: {
        styleOverrides: {
          root: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            '&:selected': {
              backgroundColor: '#003263',
              borderLeft: `8px solid #37BF59`,
              paddingLeft: '8px',
            },
          },
          gutters: {
            paddingRight: '5px',
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            fontWeight: 700,
            color: '#68737B',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            '&$active': {
              fontWeight: 700,
              color: '#68737B',
            },
            // eslint-disable-next-line @typescript-eslint/naming-convention
            '&$completed': {
              fontWeight: 700,
              color: '#68737B',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          outlined: {
            backgroundColor: '#FFFFFF',
            paddingLeft: 2,
            paddingRight: 2,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {},
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontFamily: 'Source Sans Pro',
            fontSize: 13,
            fontWeight: 700,
            lineHeight: '21px',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            '& input[type=number]': {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              '-moz-appearance': 'textfield',
            },
            // eslint-disable-next-line @typescript-eslint/naming-convention
            '& input[type=number]::-webkit-outer-spin-button': {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              '-webkit-appearance': 'none',
              margin: 0,
            },
            // eslint-disable-next-line @typescript-eslint/naming-convention
            '& input[type=number]::-webkit-inner-spin-button': {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              '-webkit-appearance': 'none',
              margin: 0,
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          '@global': {
            main: {
              overflowX: 'hidden',
            },
          },
        },
      },
      /* End Migrated */
    },
  })
)

export const BirdsongProvider: React.FC<PropsWithChildren> = ({ children }) => (
  <ThemeProvider theme={theme}>
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en-us'>
      {children}
    </LocalizationProvider>
  </ThemeProvider>
)
